import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Fade from "@mui/material/Fade";
import { fontRoboto, fontRobotoCondensed, transientOptions } from "@product/scmp-sdk";

import { BaseEntityLink } from "~/components/base-entity-link";
import { Popper } from "~/components/common/popper";
import { ZIndexLayer } from "~/lib/styles";

import { HeaderAllMenuApplications } from "./header-all-menu-applications";
import { HeaderAllMenuColumnists } from "./header-all-menu-columnists";
import { HeaderAllMenuEpaper } from "./header-all-menu-epaper";
import { HeaderAllMenuFocus } from "./header-all-menu-focus";
import { HeaderAllMenuHighlights } from "./header-all-menu-highlights";
import { HeaderAllMenuSection } from "./header-all-menu-section";
import { HeaderAllMenuTopics } from "./header-all-menu-topics";
import ArrowIcon from "./icon-arrow.svg";
import HomeArrowIcon from "./icon-home-arrow.svg";

type ToggleProps = {
  $isToggle: boolean;
};
export const StyledIconArrow = styled(ArrowIcon, { ...transientOptions })<ToggleProps>`
  transform: rotate(0deg);

  transition: transform 0.2s;
  ${props => {
    if (!props.$isToggle) return;
    return css`
      transform: rotate(180deg);
    `;
  }}
`;

export const StyledBaseEntityLink = styled(BaseEntityLink)``;

export const Title = styled.span`
  margin-inline-end: 5px;

  color: #4585ff;
  font-weight: 700;
  font-size: 15px;
  font-family: ${fontRobotoCondensed};
  line-height: 18px;
`;

export const Container = styled.div<ToggleProps>`
  display: flex;
  align-items: center;

  padding: 4px;

  background: ${props => (props.$isToggle ? "rgba(69, 133, 255, 0.2)" : "unset")};

  cursor: pointer;
`;

export const StyledPopper = styled(Popper)`
  z-index: ${ZIndexLayer.AppBar};
  ${props => props.theme.breakpoints.up("xLargeDesktop")} {
    inset-inline-start: -28px !important;

    inline-size: calc(100% + 56px) !important;
  }
`;

export type MegaMenuContainerProps = {
  $variant?: string;
};

export const MegaMenuWrapper = styled.div<{ $variant?: string }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  align-items: center;

  margin-block-start: 17px;

  background-color: #e5e5e5;

  /* for homepage, stick overlay to the line */
  ${props =>
    props.$variant !== undefined &&
    props.$variant === "home" &&
    props.theme.breakpoints.up("desktop")} {
    margin-block-start: 11px;
  }

  /* for homepage (when scrolled down), stick overlay to the line */
  ${props =>
    props.$variant !== undefined &&
    props.$variant === "home-swap" &&
    props.theme.breakpoints.up("desktop")} {
    margin-block-start: 22px;
  }

  /* for homepage, lock width under large screen */
  ${props =>
    props.$variant !== undefined &&
    ["home", "home-swap"].includes(props.$variant) &&
    props.theme.breakpoints.up("largeDesktop")} {
    inline-size: 1440px;
  }
`;

export const MegaMenuContainer = styled.div<MegaMenuContainerProps>`
  display: grid;
  grid:
    "section topics-and-focus application columnists" min-content
    "highlight highlight highlight highlight" min-content
    / auto fit-content(12%) max-content max-content;
  gap: 28px 35px;

  inline-size: 100%;
  padding-block: 32px 40px;
  border-block-end: 1px solid #000000;

  ${props => {
    switch (props.$variant) {
      case "home":
        return css`
          margin-block-start: 4px;
        `;
      case "home-swap":
        return css`
          margin-block-start: 16px;
        `;
      default:
        css`
          margin-block-start: 17px;
        `;
    }
  }}

  ${props => props.theme.breakpoints.up("mediumDesktop")} {
    grid:
      "section topics-and-focus application columnists" min-content
      "highlight highlight highlight highlight" min-content
      / auto fit-content(15%) max-content max-content;
  }

  /* for all, default dynamic width, set a fixed padding */
  ${props => props.theme.breakpoints.up("desktop")} {
    padding-inline: 48px;
  }

  /* for homepage, special handling for the padding inline */
  ${props =>
    props.$variant !== undefined &&
    ["home", "home-swap"].includes(props.$variant) &&
    props.theme.breakpoints.up("homeDesktop")} {
    padding-inline: 80px;
  }

  /* for homepage, special handling between desktop and mediumDesktop */
  ${props =>
    props.$variant !== undefined &&
    ["home", "home-swap"].includes(props.$variant) &&
    props.theme.breakpoints.between("desktop", "mediumDesktop")} {
    padding-inline: 30px;
  }

  /* lock homepage menu width under large screen */
  ${props =>
    props.$variant !== undefined &&
    ["home", "home-swap"].includes(props.$variant) &&
    props.theme.breakpoints.up("mediumDesktop")} {
    inline-size: 1208px;
    padding-inline: 0;
  }
`;

export const StyledHeaderAllMenuApplications = styled(HeaderAllMenuApplications)`
  grid-area: application;
`;
export const StyledHeaderAllMenuColumnists = styled(HeaderAllMenuColumnists)`
  grid-area: columnists;
`;
export const StyledHeaderAllMenuFocus = styled(HeaderAllMenuFocus)`
  grid-area: focus;
`;
export const StyledHeaderAllMenuEpaper = styled(HeaderAllMenuEpaper)`
  grid-area: epaper;
`;
export const StyledHeaderAllMenuSection = styled(HeaderAllMenuSection)`
  grid-area: section;
`;
export const StyledHeaderAllMenuTopics = styled(HeaderAllMenuTopics)`
  grid-area: topics;
`;

export const HeaderAllMenuTopicsAndFocus = styled.div`
  grid-area: topics-and-focus;

  display: grid;
  grid:
    "topics" min-content
    "focus" min-content
    "epaper" min-content
    / fit-content(100%);
  row-gap: 28px;
`;

export const StyledHeaderAllMenuHighlight = styled(HeaderAllMenuHighlights)`
  grid-area: highlight;
`;

export const StyledFade = styled(Fade)`
  max-inline-size: 1616px;
  margin-inline: auto;
`;

export const HomeContainer = styled.div<ToggleProps>`
  display: flex;
  align-items: center;

  padding: 0;

  background: ${props => (props.$isToggle ? "rgba(69, 133, 255, 0.2)" : "unset")};

  cursor: pointer;
`;

export const HomeTitle = styled.span`
  margin-inline-end: 4px;

  color: #4585ff;
  font-weight: 700;
  font-size: 14px;
  font-family: ${fontRoboto};
  line-height: normal;
`;

export const StyledHomeIconArrow = styled(HomeArrowIcon, { ...transientOptions })<ToggleProps>`
  transform: rotate(0deg);

  transition: transform 0.2s;
  ${props => {
    if (!props.$isToggle) return;
    return css`
      transform: rotate(180deg);
    `;
  }}
`;
